import React from 'react';
import type { WorkModelHubStats } from '../../../types/work-model-hub';
import type { HubCrossLink } from '../../../types/hub-cross-link';
import { HubCrossLinks } from '../../../components/hub/HubCrossLinks';
import { buildWorkModelHubPath } from '../../../utils/work-model/build-work-model-hub-path';
import { buildRoleHubPath } from '../../../utils/role/role-hub-config';
import { buildLocationSrpPath } from '../../../utils/location/build-location-srp-path';
import { companyProfileUrlPath } from '../../../utils/routing/company-profile-url-path';
import { buildHubIndexLinks, cityInCopy, hubLinksHeading } from '../../../utils/seo/hub-index-links';
import { getFinnishRoleGenitive } from '../../../utils/i18n/finnish-role-genitive';
import { finnishRolePhrase } from '../../../utils/seo/generate-srp-seo-meta';
import { getCategoryDisplayName } from '../../../utils/category/category-hub-config';
import { LOCALIZED_PATHS } from '../../../utils/constants/url-constants';

type Locale = 'en' | 'fi' | 'sv';

// In-code locale maps, like RoleHeader.tsx: this header is part of the server-rendered SRP shell.
const T: Record<Locale, {
  remoteH1: string;
  hybridH1: string;
  remoteRoleH1: (role: string) => string;
  hybridRoleH1: (role: string) => string;
  remoteCityH1: (city: string) => string;
  hybridCityH1: (city: string) => string;
  remoteCategoryH1: (cat: string) => string;
  hybridCategoryH1: (cat: string) => string;
  openNow: (n: string) => string;
  remoteIntro: (n: string) => string;
  hybridIntro: (n: string) => string;
  englishShare: (pct: string) => string;
  roles: string;
  fields: string;
  cities: string;
  employers: string;
  otherModel: string;
  allRemote: string;
  allHybrid: string;
  roleAll: (role: string) => string;
  cityAll: (city: string) => string;
}> = {
  fi: {
    remoteH1: 'Etätyöpaikat Suomessa',
    hybridH1: 'Hybridityöpaikat Suomessa',
    remoteRoleH1: (role) => `${finnishRolePhrase(role, 'etätyöt')}`,
    hybridRoleH1: (role) => `${finnishRolePhrase(role, 'hybridityöt')}`,
    remoteCityH1: (city) => `Etätyöpaikat ${city}`,
    hybridCityH1: (city) => `Hybridityöpaikat ${city}`,
    remoteCategoryH1: (cat) => `Etätyöt: ${cat}`,
    hybridCategoryH1: (cat) => `Hybridityöt: ${cat}`,
    openNow: (n) => `${n} avointa työpaikkaa juuri nyt`,
    remoteIntro: (n) =>
      `JobCrawls kokoaa etätyöpaikat suoraan työnantajien sivuilta ja Työmarkkinatorilta yhteen hakuun: ${n} kokonaan etänä tehtävää työtä. Ilmoitukset päivittyvät päivittäin.`,
    hybridIntro: (n) =>
      `JobCrawls kokoaa hybridityöpaikat suoraan työnantajien sivuilta ja Työmarkkinatorilta yhteen hakuun: ${n} työtä, joissa osa viikosta tehdään etänä ja osa toimistolla. Ilmoitukset päivittyvät päivittäin.`,
    englishShare: (pct) => `${pct} % ilmoituksista on englanniksi`,
    roles: 'Suosituimmat ammatit',
    fields: 'Alat',
    cities: 'Kaupungit',
    employers: 'Eniten rekrytoivat työnantajat',
    otherModel: 'Muut työmallit',
    allRemote: 'Kaikki etätyöpaikat',
    allHybrid: 'Kaikki hybridityöpaikat',
    roleAll: (role) => `Kaikki ${role} työpaikat`,
    cityAll: (city) => `Kaikki työpaikat ${city}`,
  },
  sv: {
    remoteH1: 'Distansjobb i Finland',
    hybridH1: 'Hybridjobb i Finland',
    remoteRoleH1: (role) => `${role} på distans`,
    hybridRoleH1: (role) => `${role}, hybridjobb`,
    remoteCityH1: (city) => `Distansjobb i ${city}`,
    hybridCityH1: (city) => `Hybridjobb i ${city}`,
    remoteCategoryH1: (cat) => `Distansjobb: ${cat}`,
    hybridCategoryH1: (cat) => `Hybridjobb: ${cat}`,
    openNow: (n) => `${n} lediga tjänster just nu`,
    remoteIntro: (n) =>
      `JobCrawls samlar distansjobb direkt från arbetsgivarnas sidor och Työmarkkinatori i en sökning: ${n} helt distansbaserade tjänster. Annonserna uppdateras dagligen.`,
    hybridIntro: (n) =>
      `JobCrawls samlar hybridjobb direkt från arbetsgivarnas sidor och Työmarkkinatori i en sökning: ${n} tjänster där en del av veckan görs på distans och en del på kontoret. Annonserna uppdateras dagligen.`,
    englishShare: (pct) => `${pct} % av annonserna är på engelska`,
    roles: 'Populära yrken',
    fields: 'Branscher',
    cities: 'Städer',
    employers: 'Arbetsgivare som rekryterar mest',
    otherModel: 'Andra arbetsmodeller',
    allRemote: 'Alla distansjobb',
    allHybrid: 'Alla hybridjobb',
    roleAll: (role) => `Alla jobb som ${role}`,
    cityAll: (city) => `Alla lediga jobb i ${city}`,
  },
  en: {
    remoteH1: 'Remote Jobs in Finland',
    hybridH1: 'Hybrid Jobs in Finland',
    remoteRoleH1: (role) => `Remote ${role} Jobs`,
    hybridRoleH1: (role) => `Hybrid ${role} Jobs`,
    remoteCityH1: (city) => `Remote Jobs in ${city}`,
    hybridCityH1: (city) => `Hybrid Jobs in ${city}`,
    remoteCategoryH1: (cat) => `Remote ${cat} Jobs`,
    hybridCategoryH1: (cat) => `Hybrid ${cat} Jobs`,
    openNow: (n) => `${n} open positions right now`,
    remoteIntro: (n) =>
      `JobCrawls collects remote jobs straight from employers' own sites and Työmarkkinatori into one search: ${n} fully remote positions. Listings refresh daily.`,
    hybridIntro: (n) =>
      `JobCrawls collects hybrid jobs straight from employers' own sites and Työmarkkinatori into one search: ${n} positions split between remote days and the office. Listings refresh daily.`,
    englishShare: (pct) => `${pct}% of the listings are in English`,
    roles: 'Popular roles',
    fields: 'Fields',
    cities: 'Cities',
    employers: 'Employers hiring most',
    otherModel: 'Other work models',
    allRemote: 'All remote jobs',
    allHybrid: 'All hybrid jobs',
    roleAll: (role) => `All ${role} jobs`,
    cityAll: (city) => `All jobs in ${city}`,
  },
};

function formatCount(n: number, locale: Locale): string {
  return locale === 'en' ? n.toLocaleString('en-US') : n.toLocaleString('fi-FI').replace(/ /g, ' ');
}

function roleInCopy(name: string, locale: Locale): string {
  if (locale !== 'fi') return name;
  const gen = getFinnishRoleGenitive(name);
  return gen === name.trim() ? name : gen;
}

/** H1 of a work-model page, by context (bare hub, x role, x city, x category). Exported for tests. */
export function buildWorkModelHeaderH1(stats: WorkModelHubStats, locale: Locale): string {
  const t = T[locale];
  const remote = stats.key === 'remote';
  const { role, city, category } = stats.context;
  if (role) return remote ? t.remoteRoleH1(role.name) : t.hybridRoleH1(role.name);
  if (city) return remote ? t.remoteCityH1(cityInCopy(city.name, locale)) : t.hybridCityH1(cityInCopy(city.name, locale));
  if (category) return remote ? t.remoteCategoryH1(category.name) : t.hybridCategoryH1(category.name);
  return remote ? t.remoteH1 : t.hybridH1;
}

export interface WorkModelHeaderProps {
  stats: WorkModelHubStats;
  locale: Locale;
}

/**
 * Static SEO header for the remote and hybrid hubs and their combinations: H1, live count, intro,
 * and chips to the work-model x role / x category / x city pages, the employers hiring most, the
 * other work model and the index hubs. All in the initial server HTML; rendered by SearchHeader.
 */
export const WorkModelHeader: React.FC<WorkModelHeaderProps> = ({ stats, locale }) => {
  const t = T[locale];
  const remote = stats.key === 'remote';
  const other = remote ? 'hybrid' : 'remote';
  const count = formatCount(stats.jobCount, locale);
  const { role, city, category } = stats.context;
  const categorySegment = locale === 'fi' ? 'kategoria' : locale === 'sv' ? 'kategori' : 'category';

  const roleLinks: HubCrossLink[] = stats.topRoles.map((r) => ({
    kind: 'role',
    href: buildWorkModelHubPath(stats.key, locale, { roleSlug: r.slug }),
    label: remote && locale === 'fi' ? finnishRolePhrase(r.name, 'etätyöt') : locale === 'fi' ? finnishRolePhrase(r.name, 'hybridityöt') : r.name,
    count: r.count,
  }));
  const fieldLinks: HubCrossLink[] = stats.topFields
    .filter((f) => f.categorySlug)
    .map((f) => ({
      kind: 'category',
      href: buildWorkModelHubPath(stats.key, locale, { categorySlug: f.categorySlug }),
      label: getCategoryDisplayName(f.categorySlug!, locale) ?? f.value,
      count: f.count,
    }));
  const cityLinks: HubCrossLink[] = stats.topCities.map((c) => ({
    kind: 'roleCity',
    href: buildWorkModelHubPath(stats.key, locale, { citySlug: c.slug }),
    label: c.name,
    count: c.count,
  }));
  const employerLinks: HubCrossLink[] = stats.topEmployers.map((e) => ({
    kind: 'employer',
    href: companyProfileUrlPath(locale, e.id),
    label: e.name,
    count: e.count,
  }));
  // Up-links from a combination page back to its parents, plus the other work model.
  const upLinks: HubCrossLink[] = [
    ...(role || city || category
      ? [{ kind: 'remote' as const, href: buildWorkModelHubPath(stats.key, locale), label: remote ? t.allRemote : t.allHybrid }]
      : []),
    ...(role ? [{ kind: 'role' as const, href: buildRoleHubPath(role.slug, locale), label: t.roleAll(roleInCopy(role.name, locale)) }] : []),
    ...(city ? [{ kind: 'location' as const, href: `/${locale}${buildLocationSrpPath(city.slug, locale)}`, label: t.cityAll(cityInCopy(city.name, locale)) }] : []),
    ...(category
      ? [{ kind: 'category' as const, href: `/${locale}/${LOCALIZED_PATHS[locale].jobs}/${categorySegment}/${category.slug}`, label: category.name }]
      : []),
    {
      kind: 'remote' as const,
      href: buildWorkModelHubPath(other, locale, { roleSlug: role?.slug ?? null, citySlug: city?.slug ?? null, categorySlug: category?.slug ?? null }),
      label: other === 'remote' ? t.allRemote : t.allHybrid,
    },
    ...buildHubIndexLinks(locale, ['roles', 'employers']),
  ];

  return (
    <section
      id="work-model-info-header"
      className="work-model-info-header relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-[#F8F5F0] via-[#F2EADF] to-[#E3EEF6]/70 dark:from-[#170F07] dark:via-[#2A1D0F] dark:to-[#1A2A38] p-6 sm:p-8 lg:p-12"
      aria-labelledby="work-model-header-title"
    >
      <h1 id="work-model-header-title" className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1E1A14] dark:text-[#F2EADF] mb-2">
        {buildWorkModelHeaderH1(stats, locale)}
      </h1>
      {stats.jobCount > 0 ? <p className="text-sm font-semibold text-[#3D3529] dark:text-[#C4A882] mb-3">{t.openNow(count)}</p> : null}
      <p className="text-base sm:text-lg leading-relaxed max-w-5xl text-[#3D3529] dark:text-[#C4A882] mb-2">
        {remote ? t.remoteIntro(count) : t.hybridIntro(count)}
      </p>
      {stats.englishSharePct != null && stats.englishSharePct > 0 ? (
        <p className="text-sm text-[#7A6E5F] dark:text-[#B8A48C] mb-6">{t.englishShare(String(stats.englishSharePct))}</p>
      ) : (
        <div className="mb-4" />
      )}
      <div className="space-y-5">
        {roleLinks.length > 0 ? <HubCrossLinks variant="chips" links={roleLinks} locale={locale} heading={t.roles} /> : null}
        {fieldLinks.length > 0 ? <HubCrossLinks variant="chips" links={fieldLinks} locale={locale} heading={t.fields} /> : null}
        {cityLinks.length > 0 ? <HubCrossLinks variant="chips" links={cityLinks} locale={locale} heading={t.cities} /> : null}
        {employerLinks.length > 0 ? <HubCrossLinks variant="chips" links={employerLinks} locale={locale} heading={t.employers} /> : null}
        <HubCrossLinks variant="chips" links={upLinks} locale={locale} heading={hubLinksHeading(locale)} />
      </div>
    </section>
  );
};
